// Below this bounding-box size (px), a single stroke is treated as a dot/tiny scribble
const MIN_SIGNATURE_SIZE_PX = 20;
// Below this ink length (px), a single stroke is too short to be a real signature
const MIN_STROKE_LENGTH_PX = 40;

// Feature keys used with getFeatureConfig
export const FEATURE_CONFIG = {
  SIGNATURE_PAD: 'signature-pad',
  BOL_UPLOAD: 'bol-upload',
};



const getStrokePoints = (stroke) => stroke?.points || stroke || [];

const getStrokeLength = (points) => {
  let length = 0;
  for (let i = 1; i < points.length; i += 1) {
    length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return length;
};

/**
 * Determine whether a react-signature-canvas pad holds a genuine, continuous
 * signature, i.e. at least one unbroken stroke (single pen-down to pen-up)
 * with enough spread and length, rather than isolated taps/dots or several
 * small disconnected strokes pieced together (which signature_pad still
 * reports as "not empty").
 * @param {Object} sigPad react-signature-canvas ref instance
 * @returns {boolean} true if at least one continuous stroke is a real signature
 */
export const isValidSignature = (sigPad) => {
  if (!sigPad || sigPad.isEmpty()) {
    return false;
  }

  const strokes = sigPad.toData() || [];

  return strokes.some((stroke) => {
    const points = getStrokePoints(stroke);
    if (points.length === 0) {
      return false;
    }

    const xValues = points.map((point) => point.x);
    const yValues = points.map((point) => point.y);
    const width = Math.max(...xValues) - Math.min(...xValues);
    const height = Math.max(...yValues) - Math.min(...yValues);

    return (width > MIN_SIGNATURE_SIZE_PX || height > MIN_SIGNATURE_SIZE_PX)
      && getStrokeLength(points) > MIN_STROKE_LENGTH_PX;
  });
};

/**
 * Resolve whether signature validation is enabled, giving precedence to a
 * carrier-specific override in Config.Carriers over the global Config.Enabled flag.
 * @param {Object} config getFeatureConfig `Config` payload
 * @param {string} carrierId current carrier id
 * @returns {boolean} true if signature validation should be enforced
 */
export const resolveSignatureValidationEnabled = (config, carrierId) => {
  if (!config) {
    return false;
  }

  const carrierOverride = config.Carriers?.find((carrier) => carrier.CarrierId === carrierId && carrier.Enabled === false);
  if (carrierOverride) {
    return !!carrierOverride.Enabled;
  }

  return !!config.Enabled;
};

/**
 * Resolve whether BOL upload is enabled, following the same override rules
 * as signature validation: carrier-specific false values take precedence
 * over the global `Config.Enabled` flag.
 * @param {Object} config getFeatureConfig `Config` payload
 * @param {string} carrierId current carrier id
 * @returns {boolean} true if BOL upload should be enabled
 */
export const resolveBolUploadEnabled = (config, carrierId) => {
  if (!config) {
    return false;
  }

  const carrierOverride = config.Carriers?.find((carrier) => carrier.CarrierId === carrierId && carrier.Enabled === false);
  if (carrierOverride) {
    return !!carrierOverride.Enabled;
  }

  return !!config.Enabled;
};
